import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { T, FONT_MONO, INDICATOR_TEXT } from '../../../constants/tokens';
import { lowBatteryPercent } from '../../../constants';
import DeviceHealthBar from '../../../components/DeviceHealthBar';

// Icons
import Hallway from '../../../images/customIcons/Hallway.png';
import Room from '../../../images/customIcons/Room.png';
import Door from '../../../images/customIcons/Door.png';
import BadgeIcon from '../../../images/customIcons/Badge.png';

const useStyles = makeStyles({
  card: {
    flex: '1 0 264px',
    minWidth: 0,
    background: T.elevated,
    border: '1px solid ' + T.hairline,
    borderRadius: 6,
    padding: 24,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': {
      background: T.surfaceSoft,
      borderColor: T.muted
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.ink,
      outlineOffset: 2
    }
  },
  rail: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 24,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    padding: 2
  }
});

const ICON = { Room: Room, Door: Door, Hallway: Hallway, Badge: BadgeIcon };

/**
 * The device health rail at the top of the Devices screen, above the tabs.
 *
 * One card per hub type plus one for badges: how many of each are reporting,
 * as a fraction and a percentage, with a word for whether that needs doing
 * anything about. Clicking a card filters the list to it, which is the whole
 * point of putting a summary above a list.
 */
export default function DeviceHealth({ hubs, badges, staffCount, onSelect, onOpenDevice }) {
  const classes = useStyles();

  const groups = ['Room', 'Door', 'Hallway'].map((type) => {
    const of = hubs.filter((hub) => hub.getHubType() === type);
    const online = of.filter((hub) => hub.getIsConnected()).length;
    const offline = of.filter((hub) => !hub.getIsConnected());
    return {
      counts: { ok: online, bad: offline.length },
      issues: offline.map((hub) => ({
        key: hub.getSerialNumber(),
        label: hub.getSerialNumber() + (hub.getRoom()?.getIdentifier() ? ' · ' + hub.getRoom().getIdentifier() : ' · offline'),
        tone: 'bad',
        onOpen: () => onOpenDevice('Hubs', hub.getSerialNumber())
      })),
      key: type,
      icon: type,
      name: type + ' hubs',
      sub: 'Hub · ' + type.toLowerCase(),
      online,
      total: of.length,
      note: of.length - online > 0
        ? (of.length - online) + ' not connected'
        : 'All hubs reporting',
      tab: 'Hubs',
      filter: { category: 'Hub Type', value: type }
    };
  });

  const badgesConnected = badges.filter((b) => b.getIsConnected()).length;
  const isLow = (b) => b.getBatteryLevel() != null && b.getBatteryLevel() < lowBatteryPercent();
  const lowBadges = badges.filter(isLow).length;
  const badgesOffline = badges.filter((b) => !b.getIsConnected());
  const badgesLowOnline = badges.filter((b) => b.getIsConnected() && isLow(b));
  const unassigned = Math.max(0, staffCount - badges.length);

  const cards = groups.concat([{
    key: 'Badges',
    icon: 'Badge',
    name: 'Badges',
    sub: 'Badge · ' + unassigned + ' staff unassigned',
    online: badgesConnected,
    total: badges.length,
    note: lowBadges > 0
      ? lowBadges + ' below ' + lowBatteryPercent() + '% power'
      : 'All badges reporting',
    // Low power is not disconnection, but it still needs doing something
    // about, so it counts towards the card's verdict.
    needsAction: lowBadges > 0,
    tab: 'Badges',
    filter: null,
    counts: { ok: badgesConnected - badgesLowOnline.length, warn: badgesLowOnline.length, bad: badgesOffline.length },
    // Offline first, then low: worst first, as the chips read left to right.
    issues: badgesOffline.map((b) => ({
      key: b.getSerialNumber(),
      label: b.getSerialNumber() + ' · offline',
      tone: 'bad',
      onOpen: () => onOpenDevice('Badges', b.getSerialNumber())
    })).concat(badgesLowOnline.map((b) => ({
      key: b.getSerialNumber(),
      label: b.getSerialNumber() + ' · ' + Math.floor(b.getBatteryLevel()) + '%',
      tone: 'warn',
      onOpen: () => onOpenDevice('Badges', b.getSerialNumber())
    })))
  }]);

  return (
    <div style={{ marginBottom: 32 }}>
      <div className={classes.rail} data-scroller role='group' aria-label='Device health'>
        {cards.map((card) => {
          const off = card.total - card.online;
          const pct = card.total > 0 ? Math.round((card.online / card.total) * 100) : 0;
          const action = off > 0 || card.needsAction;
          const accent = off > 0
            ? INDICATOR_TEXT.error
            : (card.needsAction ? INDICATOR_TEXT.warning : INDICATOR_TEXT.success);

          return (
            // Not a <button>: the health chips inside it are buttons.
            <div
              role='button'
              tabIndex={0}
              key={card.key}
              className={classes.card}
              onClick={() => onSelect(card.tab, card.filter)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(card.tab, card.filter);
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <img src={ICON[card.icon]} alt='' style={{ width: 28, height: 28, flex: 'none', objectFit: 'contain' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, lineHeight: '24px', color: T.ink }}>
                    {card.name}
                  </div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.body }}>
                    {card.sub}
                  </div>
                </div>
                <span
                  style={{
                    flex: 'none',
                    fontFamily: FONT_MONO,
                    fontSize: 12,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    color: accent
                  }}
                >
                  {action ? 'Action' : 'Nominal'}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: 32, fontWeight: 500, lineHeight: '40px', color: T.ink }}>
                  {card.online}
                </span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 14, color: T.body }}>
                  / {card.total} connected
                </span>
                <div style={{ flex: 1 }} />
                <span style={{ fontFamily: FONT_MONO, fontSize: 14, color: accent }}>
                  {pct}%
                </span>
              </div>

              <div style={{ fontSize: 14, lineHeight: '20px', color: T.body, marginTop: 8 }}>
                {card.note}
              </div>

              <DeviceHealthBar
                counts={card.counts}
                issues={card.issues}
                onMore={() => onSelect(card.tab, card.filter)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

DeviceHealth.propTypes = {
  hubs: PropTypes.array.isRequired,
  badges: PropTypes.array.isRequired,
  staffCount: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  // Open one device: (tab, serial number)
  onOpenDevice: PropTypes.func.isRequired
};
